import { useEffect, useState } from 'react'
import { ChannelPlayer, useAudioEngine } from 'player'
import {
  addInstrumentsProcessingV2,
  generateTemporaryChannelsV2,
  getFilesStemsV2,
  needLimitForMetronomeV2
} from './utils/summary'
import { getWavesUrl } from './utils'
import {
  ChannelList,
  FilesStems,
  FilesWithWaves,
  UseChannelsProps
} from './utils/types'

const setWavesUrl = (filesStems: FilesStems[]): FilesWithWaves[] => {
  return filesStems?.map(({ id, url, bucket, needTranscode }) => ({
    id,
    url,
    bucket,
    wavesUrl: getWavesUrl(needTranscode ? url : bucket, !!needTranscode)
  }))
}

const setChannelsList = ({
  // audioEngine,
  // readyToMetronome,
  channelsUrls,
  summary
}: ChannelList): ChannelPlayer[] => {
  if (!channelsUrls.length) {
    const temporary = generateTemporaryChannelsV2(summary)
    return temporary
  }

  const separate = summary?.separate
  const beatschords = summary?.beatschords
  let list = channelsUrls as ChannelPlayer[]

  if (separate?.stems?.some((i) => i?.adding)) {
    list = addInstrumentsProcessingV2(list, separate)
  }

  if (['FAILED', 'STARTED', 'QUEUED'].includes(beatschords?.status || '')) {
    list.push({
      id: 'metronome',
      error: beatschords?.status === 'FAILED',
      processing: beatschords?.status !== 'FAILED'
    })
  }

  return list
}

export const useChannels = ({
  enabled,
  limited,
  summary
}: UseChannelsProps): ChannelPlayer[] => {
  const audioEngine = useAudioEngine((p: any) => p.engine)
  const readyToMetronome = useAudioEngine((p: any) => p.state.isReadyToPlay)
  const [channels, setChannels] = useState<ChannelPlayer[]>([])

  useEffect(() => {
    if (!enabled) return

    const instruments = summary?.separate?.stems ?? []
    const metronomes = summary?.beatschords?.metronome ?? []

    const instrumentsReady = instruments?.filter(
      (instrument) => instrument.adding === false
    )

    const isOriginalMix =
      instrumentsReady?.length === 1 && instrumentsReady[0]?.stem === 'other'

    const stems = [
      ...instruments
        .filter((i) => i.path && i.url)
        .map((i) => ({
          ...i,
          stem: isOriginalMix && i.stem === 'other' ? 'original_mix' : i.stem
        })),
      ...metronomes?.filter((i) => i.path && i.url)
    ]

    const beatschords = summary?.beatschords
    const limitedMetronome = needLimitForMetronomeV2({
      limited,
      duration: summary?.original?.duration,
      songProcessedByPlan: beatschords?.params?.plan,
      maxDuration: beatschords?.params?.maxDuration
    })

    getFilesStemsV2({
      stems,
      limitedMetronome,
      duration: summary?.original?.duration
    })
      .then(setWavesUrl)
      .then((channelsUrls) =>
        setChannelsList({
          channelsUrls,
          summary,
          audioEngine,
          readyToMetronome
        })
      )
      .then((_channels) => setChannels(_channels))
  }, [limited, enabled, summary, audioEngine, readyToMetronome])

  return channels
}
